import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function formatoPesos(value) {
    return '$ ' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function FormatoFechaSinHora(value) {
    const fecha = new Date(value);
    return format(fecha, 'dd-MMMM-yyyy', { locale: es }).toUpperCase();
}

function FormatoFechaConHora(value) {
    const fecha = new Date(value);
    return format(fecha, "dd-MMMM-yyyy HH:mm:ss", { locale: es }).toUpperCase();
}

function calcularAnchoProporcional(base64, alturaFija) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const ancho = img.naturalWidth && img.naturalHeight
                ? (img.naturalWidth * alturaFija) / img.naturalHeight
                : 50;
            resolve(ancho);
        };
        img.onerror = () => resolve(50);
        img.src = base64;
    });
}

function detectarFormatoImagen(base64) {
    if (base64.startsWith('data:image/png'))  return 'PNG';
    if (base64.startsWith('data:image/jpeg')) return 'JPEG';
    if (base64.startsWith('data:image/jpg'))  return 'JPEG';
    if (base64.startsWith('data:image/gif'))  return 'GIF';
    if (base64.startsWith('data:image/webp')) return 'WEBP';
    return 'PNG';
}

const LibroDiarioPDF = async (objLibroDiario, objEmpresa, fechaI, fechaF) => {
    return new Promise(async (resolve, reject) => {
        try {
            const doc = new jsPDF({ format: 'letter' });

            const color               = objEmpresa?.color || '#662e91';
            const logoBase64          = objEmpresa?.logo?.base64 || '';
            const fechaHoraGeneracion = FormatoFechaConHora(new Date());

            const margenIzq = 12;
            const margenDer = 12;
            const anchoUtil = 215 - margenIzq - margenDer; // ~191mm
            const xDer      = 215 - margenDer;

            // ── CABECERA ──────────────────────────────────────────────────
            let y = 14;

            if (logoBase64 !== '') {
                const alturaLogo = 20;
                const formatoImg = detectarFormatoImagen(logoBase64);
                const anchoLogo  = await calcularAnchoProporcional(logoBase64, alturaLogo);
                doc.addImage(logoBase64, formatoImg, margenIzq, y, anchoLogo, alturaLogo);
            }

            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('Libro Diario', xDer, y, { align: 'right' });

            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text((objEmpresa?.rfc || '') + ' | ' + (objEmpresa?.nombre || ''), xDer, y += 6, { align: 'right' });

            const periodo = 'PERIODO DEL ' + FormatoFechaSinHora(fechaI) + ' AL ' + FormatoFechaSinHora(fechaF);
            doc.text(periodo, xDer, y += 5, { align: 'right' });

            y += 10;

            // ── PÓLIZAS ───────────────────────────────────────────────────
            // Construimos el body de autoTable agrupando cabecera de póliza + asientos
            const body = [];

            for (let a of objLibroDiario) {
                // Fila de cabecera de póliza (con fondo de color más claro)
                body.push([
                    {
                        content: (a.tipo || '') + ' ' + (a.numPoliza || ''),
                        styles: { fontStyle: 'bold', fillColor: '#ede7f6', textColor: color }
                    },
                    {
                        content: FormatoFechaSinHora(a.fecha),
                        styles: { fontStyle: 'bold', fillColor: '#ede7f6', textColor: color }
                    },
                    {
                        content: a.descripcion || '',
                        styles: { fontStyle: 'bold', fillColor: '#ede7f6', textColor: color }
                    },
                    {
                        content: formatoPesos(a.totalCargo),
                        styles: { fontStyle: 'bold', fillColor: '#ede7f6', textColor: color, halign: 'right' }
                    },
                    {
                        content: formatoPesos(a.totalAbono),
                        styles: { fontStyle: 'bold', fillColor: '#ede7f6', textColor: color, halign: 'right' }
                    },
                ]);

                // Filas de asientos de la póliza
                for (let d of a.asientos) {
                    body.push([
                        { content: d.numCuenta || '',          styles: { halign: 'left',  fontSize: 8 } },
                        { content: '',                          styles: { halign: 'left',  fontSize: 8 } },
                        { content: d.descripcionCuenta || '',  styles: { halign: 'left',  fontSize: 8 } },
                        { content: formatoPesos(d.cargo),      styles: { halign: 'right', fontSize: 8 } },
                        { content: formatoPesos(d.abono),      styles: { halign: 'right', fontSize: 8 } },
                    ]);
                }
            }

            doc.autoTable({
                startY: y,
                head: [[
                    { content: 'Tipo/Num',    styles: { halign: 'left'  } },
                    { content: 'Fecha',       styles: { halign: 'left'  } },
                    { content: 'Descripción', styles: { halign: 'left'  } },
                    { content: 'Cargo',       styles: { halign: 'right' } },
                    { content: 'Abono',       styles: { halign: 'right' } },
                ]],
                body,
                styles: {
                    fontSize: 7,
                    cellPadding: 2,
                    overflow: 'linebreak',
                },
                headStyles: {
                    fillColor: color,
                    textColor: '#ffffff',
                    fontStyle: 'bold',
                },
                columnStyles: {
                    0: { cellWidth: 22 },   // Tipo/Num
                    1: { cellWidth: 28 },   // Fecha
                    2: { cellWidth: 95 },   // Descripción
                    3: { cellWidth: 23 },   // Cargo
                    4: { cellWidth: 23 },   // Abono
                },
                margin: { left: margenIzq, right: margenDer },
                tableWidth: anchoUtil,
            });

            // ── PIE DE PÁGINA ─────────────────────────────────────────────
            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(8);
                doc.setTextColor(150);

                const pageH = doc.internal.pageSize.getHeight();
                const pageW = doc.internal.pageSize.getWidth();

                doc.setDrawColor(color);
                doc.setLineWidth(0.3);
                doc.line(margenIzq, pageH - 16, pageW - margenDer, pageH - 16);

                doc.text('Generado: ' + fechaHoraGeneracion, margenIzq, pageH - 8, { align: 'left' });
                doc.text('Documento generado por ContaGo', pageW / 2, pageH - 8, { align: 'center' });
                doc.text('Página ' + i + ' de ' + totalPages, pageW - margenDer, pageH - 8, { align: 'right' });
            }

            const base64 = doc.output('datauristring');
            resolve(base64);
        } catch (error) {
            console.error(error);
            reject(error);
        }
    });
};

export { LibroDiarioPDF };